/**
 * One dimension's value tree, nested by `parent_id` (`DimensionList.tsx` mounts one per
 * dimension). "Não identificado" is a real, `locked` value the API always returns -- rendered
 * last among its siblings, with a lock icon and no action menu, never synthesised here.
 *
 * Reparenting drags with native HTML5 DnD (`draggable`, `onDragStart`/`onDragOver`/`onDrop`):
 * this codebase carries no drag-and-drop dependency, and a value tree a few dozen rows deep
 * needs nothing a library would add. The same move also reaches through "Mover para…", a
 * keyboard-operable picker dialog, since a mouse drag alone leaves no way for a keyboard user to
 * reparent a value.
 */
import { useId, useState, type DragEvent, type FormEvent, type ReactNode } from 'react';
import { CircleHelp, Ellipsis, Lock, Plus } from 'lucide-react';

import type { ApiFailure } from '../../../lib/http/errors';
import { getLocale } from '../../../i18n';
import { integer } from '../../../i18n/format';
import { ConfirmDialog } from '../../../chrome/form';
import { HintedIcon } from '../../../chrome/hint';
import { FormDialog } from '../../../chrome/form/FormDialog';
import { fieldErrorOf } from '../../../chrome/form/fieldErrorOf';
import { Button } from '../../../ui/button';
import { Chip } from '../../../ui/chip';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../ui/dropdown-menu';
import { Field, FieldError, FieldLabel } from '../../../ui/field';
import { Input } from '../../../ui/input';
import { cn } from '../../../ui/utils';
import { useCreateValue, useDeleteValue, useMergeValue, useUpdateValue } from './api/queries';
import type { OntologyScope, OntologyValue } from './api/schemas';
import { om, omPlural } from './messages';
import { ValuePicker } from './ValuePicker';

function subtreeIds(rootId: string, values: OntologyValue[]): Set<string> {
  const childrenOf = new Map<string, string[]>();
  for (const value of values) {
    if (value.parent_id === null) continue;
    const siblings = childrenOf.get(value.parent_id) ?? [];
    siblings.push(value.id);
    childrenOf.set(value.parent_id, siblings);
  }
  const ids = new Set<string>();
  const stack = [rootId];
  while (stack.length > 0) {
    const id = stack.pop()!;
    ids.add(id);
    stack.push(...(childrenOf.get(id) ?? []));
  }
  return ids;
}

/** A single name field, reused for renaming a value and for creating one (a top-level
 *  "Adicionar valor" or a row's "Adicionar filho") -- the only difference is which mutation
 *  the submit calls. */
function NameDialog(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  submitLabel: string;
  initialValue?: string;
  pending: boolean;
  failure: ApiFailure | null;
  requiredMessage: string;
  onSubmit: (name: string) => void | Promise<unknown>;
}): ReactNode {
  const { open, onOpenChange, initialValue = '' } = props;
  const [name, setName] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);
  const nameId = useId();

  function handleOpenChange(next: boolean): void {
    if (next) {
      setName(initialValue);
      setError(null);
    }
    onOpenChange(next);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void | Promise<unknown> {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed === '') {
      setError(props.requiredMessage);
      return;
    }
    return props.onSubmit(trimmed);
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={props.title}
      submitLabel={props.submitLabel}
      pending={props.pending}
      failure={props.failure}
      onSubmit={handleSubmit}
    >
      <Field data-invalid={error !== null || undefined}>
        <FieldLabel htmlFor={nameId}>{om('values.new.name.label')}</FieldLabel>
        <Input id={nameId} value={name} onChange={(event) => setName(event.target.value)} />
        <FieldError>{error ?? fieldErrorOf(props.failure, 'name')}</FieldError>
      </Field>
    </FormDialog>
  );
}

function AliasesDialog(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: OntologyValue;
  pending: boolean;
  failure: ApiFailure | null;
  onSubmit: (aliases: string[]) => void | Promise<unknown>;
}): ReactNode {
  const { open, onOpenChange, value } = props;
  const [aliases, setAliases] = useState<string[]>(value.aliases);
  const [draft, setDraft] = useState('');

  function handleOpenChange(next: boolean): void {
    if (next) {
      setAliases(value.aliases);
      setDraft('');
    }
    onOpenChange(next);
  }

  function addAlias(): void {
    const trimmed = draft.trim();
    if (trimmed === '' || aliases.includes(trimmed)) return;
    setAliases([...aliases, trimmed]);
    setDraft('');
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={om('values.aliases.title', { name: value.name })}
      submitLabel={om('flags.save')}
      pending={props.pending}
      failure={props.failure}
      onSubmit={(event) => {
        event.preventDefault();
        return props.onSubmit(aliases);
      }}
    >
      <Field>
        <FieldLabel>{om('values.aliases.label')}</FieldLabel>
        <div className="flex flex-wrap gap-1.5">
          {aliases.map((alias) => (
            <Chip
              key={alias}
              closeLabel={om('values.aliases.remove', { alias })}
              onClose={() => setAliases(aliases.filter((candidate) => candidate !== alias))}
            >
              {alias}
            </Chip>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            value={draft}
            placeholder={om('values.aliases.add.placeholder')}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return;
              event.preventDefault();
              addAlias();
            }}
          />
          <Button type="button" variant="outline" onClick={addAlias}>
            {om('values.aliases.add')}
          </Button>
        </div>
      </Field>
    </FormDialog>
  );
}

function MoveValueDialog(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: OntologyValue;
  values: OntologyValue[];
  onMove: (parentId: string | null) => void;
}): ReactNode {
  const { open, onOpenChange, value, values } = props;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{om('values.move.title', { name: value.name })}</DialogTitle>
        </DialogHeader>
        <ValuePicker
          values={values}
          exclude={subtreeIds(value.id, values)}
          allowTopLevel
          onSelect={(parentId) => {
            props.onMove(parentId);
            onOpenChange(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

/** Two steps in one open/close cycle: the picker names the target, then a `ConfirmDialog`
 *  states what merging actually does before it happens -- the same warn-before-destroy shape
 *  `TeamActions`' removal confirm uses, just with the target named inline in the description
 *  instead of fixed copy. */
function MergeValueDialog(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: OntologyValue;
  values: OntologyValue[];
  pending: boolean;
  failure: ApiFailure | null;
  onConfirm: (targetId: string) => void | Promise<unknown>;
}): ReactNode {
  const { open, onOpenChange, value, values } = props;
  const [target, setTarget] = useState<OntologyValue | null>(null);

  function handleOpenChange(next: boolean): void {
    if (!next) setTarget(null);
    onOpenChange(next);
  }

  if (target === null) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{om('values.merge.title', { name: value.name })}</DialogTitle>
          </DialogHeader>
          <ValuePicker
            values={values}
            exclude={new Set([value.id])}
            onSelect={(id) => setTarget(values.find((candidate) => candidate.id === id) ?? null)}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={om('values.merge.title', { name: value.name })}
      description={om('values.merge.description', { target: target.name })}
      confirmLabel={om('values.merge.confirm')}
      pending={props.pending}
      failure={props.failure}
      onConfirm={() => props.onConfirm(target.id)}
    />
  );
}

function ValueRow(props: {
  value: OntologyValue;
  values: OntologyValue[];
  dimensionId: string;
  depth: number;
  tenantId: string;
  scope: OntologyScope;
  draggingId: string | null;
  onDragStateChange: (id: string | null) => void;
}): ReactNode {
  const { value, values, dimensionId, depth, tenantId, scope } = props;
  const [dialog, setDialog] = useState<
    'rename' | 'aliases' | 'addChild' | 'move' | 'merge' | 'delete' | null
  >(null);
  const updateMutation = useUpdateValue(tenantId, scope);
  const createMutation = useCreateValue(tenantId, scope);
  const deleteMutation = useDeleteValue(tenantId, scope);
  const mergeMutation = useMergeValue(tenantId, scope);
  const locale = getLocale();

  const dragOver = props.draggingId !== null && props.draggingId !== value.id && !value.locked;

  function handleDragStart(event: DragEvent<HTMLDivElement>): void {
    event.dataTransfer.setData('text/plain', value.id);
    event.dataTransfer.effectAllowed = 'move';
    props.onDragStateChange(value.id);
  }

  function handleDrop(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    const draggedId = event.dataTransfer.getData('text/plain');
    props.onDragStateChange(null);
    if (draggedId === '' || draggedId === value.id || value.locked) return;
    if (subtreeIds(draggedId, values).has(value.id)) return; // would create a cycle
    updateMutation.mutate({ valueId: draggedId, body: { parent_id: value.id } });
  }

  return (
    <div
      role="treeitem"
      aria-selected={false}
      style={{ paddingLeft: depth * 20 }}
      className="flex flex-col"
    >
      <div
        draggable={!value.locked}
        onDragStart={handleDragStart}
        onDragEnd={() => props.onDragStateChange(null)}
        onDragOver={(event) => {
          if (!dragOver) return;
          event.preventDefault();
        }}
        onDrop={handleDrop}
        className={cn(
          'group flex items-center gap-2 rounded-md py-1.5 pr-1 pl-2',
          dragOver && 'bg-accent',
        )}
      >
        {value.locked && <Lock aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />}
        <span className="text-body-sm text-foreground">{value.name}</span>
        {value.aliases.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {value.aliases.map((alias) => (
              <Chip key={alias} size="sm">
                {alias}
              </Chip>
            ))}
          </div>
        )}
        <span className="ml-auto flex items-center gap-1 text-caption text-muted-foreground">
          {omPlural(
            scope === 'profile' ? 'values.readings.other' : 'values.mentions.other',
            value.mentions,
            {
              n: integer(value.mentions, locale),
            },
          )}
          {scope === 'profile' && (
            <HintedIcon label={om('values.readings.hint')}>
              <CircleHelp aria-hidden className="size-3.5" />
            </HintedIcon>
          )}
        </span>
        {!value.locked && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={om('values.actions.label', { name: value.name })}
              >
                <Ellipsis aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setDialog('rename')}>
                {om('values.actions.rename')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setDialog('aliases')}>
                {om('values.actions.editAliases')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setDialog('addChild')}>
                <Plus aria-hidden />
                {om('values.actions.addChild')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setDialog('move')}>
                {om('values.actions.move')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setDialog('merge')}>
                {om('values.actions.merge')}
              </DropdownMenuItem>
              <DropdownMenuItem variant="destructive" onSelect={() => setDialog('delete')}>
                {om('values.actions.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <NameDialog
        open={dialog === 'rename'}
        onOpenChange={(next) => setDialog(next ? 'rename' : null)}
        title={om('values.rename.title')}
        submitLabel={om('flags.save')}
        initialValue={value.name}
        pending={updateMutation.isPending}
        failure={updateMutation.error?.failure ?? null}
        requiredMessage={om('values.new.name.required')}
        onSubmit={(name) => updateMutation.mutateAsync({ valueId: value.id, body: { name } })}
      />
      <AliasesDialog
        open={dialog === 'aliases'}
        onOpenChange={(next) => setDialog(next ? 'aliases' : null)}
        value={value}
        pending={updateMutation.isPending}
        failure={updateMutation.error?.failure ?? null}
        onSubmit={(aliases) => updateMutation.mutateAsync({ valueId: value.id, body: { aliases } })}
      />
      <NameDialog
        open={dialog === 'addChild'}
        onOpenChange={(next) => setDialog(next ? 'addChild' : null)}
        title={om('values.new.title')}
        submitLabel={om('values.new.submit')}
        pending={createMutation.isPending}
        failure={createMutation.error?.failure ?? null}
        requiredMessage={om('values.new.name.required')}
        onSubmit={(name) => createMutation.mutateAsync({ dimensionId, name, parent_id: value.id })}
      />
      <MoveValueDialog
        open={dialog === 'move'}
        onOpenChange={(next) => setDialog(next ? 'move' : null)}
        value={value}
        values={values}
        onMove={(parentId) =>
          updateMutation.mutate({ valueId: value.id, body: { parent_id: parentId } })
        }
      />
      <MergeValueDialog
        open={dialog === 'merge'}
        onOpenChange={(next) => setDialog(next ? 'merge' : null)}
        value={value}
        values={values}
        pending={mergeMutation.isPending}
        failure={mergeMutation.error?.failure ?? null}
        onConfirm={(into) => mergeMutation.mutateAsync({ valueId: value.id, into })}
      />
      <ConfirmDialog
        open={dialog === 'delete'}
        onOpenChange={(next) => {
          setDialog(next ? 'delete' : null);
          if (!next) deleteMutation.reset();
        }}
        title={om('values.delete.title', { name: value.name })}
        description={om('values.delete.description')}
        confirmLabel={om('values.delete.confirm')}
        destructive
        pending={deleteMutation.isPending}
        failure={deleteMutation.error?.failure ?? null}
        onConfirm={() => deleteMutation.mutateAsync(value.id)}
      />
    </div>
  );
}

export function ValueTree(props: {
  dimensionId: string;
  values: OntologyValue[];
  tenantId: string;
  scope: OntologyScope;
}): ReactNode {
  const { dimensionId, values, tenantId, scope } = props;
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [addingTop, setAddingTop] = useState(false);
  const createMutation = useCreateValue(tenantId, scope);
  const reparentMutation = useUpdateValue(tenantId, scope);

  // A Perfis dimension keeps the server's order, which follows its scale (age bands youngest
  // first); every other sorts by name. "Não identificado" is last in both.
  function childrenOf(parentId: string | null): OntologyValue[] {
    return values
      .filter((value) => value.parent_id === parentId)
      .sort((a, b) => {
        if (a.locked !== b.locked) return a.locked ? 1 : -1;
        return scope === 'profile' ? 0 : a.name.localeCompare(b.name, 'pt-BR');
      });
  }

  function renderNode(value: OntologyValue, depth: number): ReactNode {
    return (
      <div key={value.id}>
        <ValueRow
          value={value}
          values={values}
          dimensionId={dimensionId}
          depth={depth}
          tenantId={tenantId}
          scope={scope}
          draggingId={draggingId}
          onDragStateChange={setDraggingId}
        />
        {childrenOf(value.id).map((child) => renderNode(child, depth + 1))}
      </div>
    );
  }

  // The tree's own background is a drop target too, for dragging a nested value back to the
  // top level -- the same gesture a file manager gives a folder's own empty space.
  const rootDragOver = draggingId !== null;

  return (
    <div role="tree" className="flex flex-col gap-0.5">
      <div
        onDragOver={(event) => {
          if (!rootDragOver) return;
          event.preventDefault();
        }}
        onDrop={(event) => {
          event.preventDefault();
          const draggedId = event.dataTransfer.getData('text/plain');
          setDraggingId(null);
          if (draggedId === '') return;
          const dragged = values.find((value) => value.id === draggedId);
          if (dragged === undefined || dragged.parent_id === null || dragged.locked) return;
          reparentMutation.mutate({ valueId: draggedId, body: { parent_id: null } });
        }}
        className="flex min-h-8 flex-col gap-0.5"
      >
        {childrenOf(null).map((value) => renderNode(value, 0))}
      </div>
      <div>
        <Button type="button" variant="ghost" size="sm" onClick={() => setAddingTop(true)}>
          <Plus aria-hidden />
          {om('values.add')}
        </Button>
      </div>
      <NameDialog
        open={addingTop}
        onOpenChange={setAddingTop}
        title={om('values.new.title')}
        submitLabel={om('values.new.submit')}
        pending={createMutation.isPending}
        failure={createMutation.error?.failure ?? null}
        requiredMessage={om('values.new.name.required')}
        onSubmit={(name) => createMutation.mutateAsync({ dimensionId, name })}
      />
    </div>
  );
}
